import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("okn-remember-email") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() =>
    Boolean(localStorage.getItem("okn-remember-email"))
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotDone, setForgotDone] = useState("");

  const sendTemporary = async () => {
    setForgotBusy(true);
    setForgotDone("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotDone(data.message);
    } catch (error) {
      setForgotDone(apiError(error));
    } finally {
      setForgotBusy(false);
    }
  };

  useEffect(() => {
    // Skip the redirect while the transition overlay is playing.
    if (user && !leaving) navigate("/", { replace: true });
  }, [user, leaving, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const result = await login(email, password, remember);
    if (!result.ok) {
      setBusy(false);
      setError(result.error);
      return;
    }
    // Short, deliberate handoff: blur out the form, flash the logo, then hand over.
    setLeaving(true);
    setTimeout(() => navigate("/", { replace: true }), 520);
  };

  return (
    <div className="relative flex min-h-screen bg-[#EEF2F8] okn-grain">
      {leaving ? (
        <div
          data-testid="login-transition-overlay"
          className="fixed inset-0 z-50 grid place-items-center bg-[#EEF2F8]"
        >
          <div className="okn-splash">
            <Logo size={72} />
          </div>
        </div>
      ) : null}
      <div className="hidden flex-1 flex-col justify-between p-14 lg:flex">
        <div>
          <Logo size={54} />
          <p className="mono mt-2 text-[11px] uppercase tracking-[0.24em] text-slate-500">
            Powered by BIM Department
          </p>
        </div>
        <div className="max-w-xl okn-rise">
          <p className="mono mb-5 text-xs uppercase tracking-[0.32em] text-cyan-600">
            Façade Project Intelligence
          </p>
          <h1 className="font-display font-semibold leading-[1.05] tracking-tight">
            <span className="block w-fit text-slate-500 text-[39px] sm:text-[49px] lg:text-[61px]">
              Every facade.
            </span>
            <span className="block w-fit text-slate-500 text-[42px] sm:text-[53px] lg:text-[65px]">
              Every status.
            </span>
            <span className="block w-fit text-[47px] sm:text-[60px] lg:text-[73px]">One model.</span>
          </h1>
          <p className="okn-script mt-8 whitespace-nowrap text-3xl leading-snug text-cyan-700 sm:text-4xl lg:text-[42px]">
            From design to site, always in sync.
          </p>
        </div>
        <p className="mono text-[11px] text-slate-500">
          © 2026 Okyanus Grup · v1.5
        </p>
      </div>

      <div className="flex w-full items-center justify-center border-slate-200 p-6 lg:w-[460px] lg:border-l lg:bg-white lg:p-12">
        <form
          onSubmit={submit}
          className={`w-full max-w-sm ${leaving ? "okn-leave" : "okn-rise"}`}
          data-testid="login-form"
        >
          <div className="mb-9 flex justify-center lg:hidden">
            <Logo size={42} />
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="mt-2 text-sm text-slate-600">Use your company account.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="mono text-[11px] uppercase tracking-wider text-slate-600">
                Company email or username
              </Label>
              <Input
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@okyanusgrup.com"
                className="mt-2 border-slate-200 bg-[#EEF2F8] text-slate-900 placeholder:text-slate-400"
                required
              />
            </div>
            <div>
              <Label className="mono text-[11px] uppercase tracking-wider text-slate-600">
                Password
              </Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 border-slate-200 bg-[#EEF2F8] text-slate-900"
                required
              />
            </div>
          </div>

          <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input
              data-testid="login-remember-checkbox"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 accent-cyan-600"
            />
            Remember me for 30 days
          </label>

          <button
            data-testid="forgot-password-link"
            type="button"
            onClick={() => {
              setForgotEmail(email);
              setForgotOpen(true);
              setForgotDone("");
            }}
            className="mt-3 text-sm text-cyan-600 hover:underline"
          >
            Forgot password?
          </button>

          {error ? (
            <p
              data-testid="login-error"
              className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </p>
          ) : null}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="mt-7 h-11 w-full rounded-full bg-cyan-500 font-medium text-[#04121a] hover:bg-cyan-400"
          >
            {busy ? <Loader2 className="animate-spin" size={17} /> : "Sign in"}
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            No account?{" "}
            <Link data-testid="go-register-link" to="/register" className="text-cyan-600 hover:underline">
              Register
            </Link>
          </p>
        </form>
      </div>

      {forgotOpen ? (
        <div
          data-testid="forgot-password-dialog"
          className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4"
        >
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 okn-card-shadow">
            <p className="font-display text-lg font-semibold text-slate-900">Forgot password</p>
            <p className="mt-2 text-sm text-slate-600">
              Enter your registered email. We will send you a temporary password — you will set a
              new one right after signing in.
            </p>
            <Input
              data-testid="forgot-email-input"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="name@okyanusgrup.com"
              className="mt-4 h-10 border-slate-200 bg-[#EEF2F8] text-sm"
            />
            {forgotDone ? (
              <p
                data-testid="forgot-password-result"
                className="mt-3 rounded-md border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-800"
              >
                {forgotDone}
              </p>
            ) : null}
            <div className="mt-5 flex gap-2">
              <Button
                data-testid="forgot-cancel-button"
                variant="ghost"
                onClick={() => setForgotOpen(false)}
                className="h-10 flex-1 rounded-full text-sm text-slate-600"
              >
                Close
              </Button>
              <Button
                data-testid="forgot-submit-button"
                onClick={sendTemporary}
                disabled={forgotBusy || !forgotEmail.includes("@")}
                className="h-10 flex-1 rounded-full bg-cyan-600 text-sm text-white hover:bg-cyan-700"
              >
                {forgotBusy ? <Loader2 className="animate-spin" size={16} /> : "Send password"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
